import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Auth } from 'aws-amplify';
import BoardCard from '../BoardCard';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import calendar from '../../icons/calendar.svg';
import DualAccountModal from '../../scenes/AdvisorDashboardV2/DualAccountModal';
import { AccountType } from '../../services/utils/types';
import IncludedChip from './IncludedChip';

const MyAdvisoryBoards = (props) => {
  const { scrollClick = null } = props;
  const { accountDetails, isDualUser, authenticate } = useAccount();

  const [memberBoards, setMemberBoards] = useState(
    accountDetails?.boards?.memberBoards || []
  );
  const [hideScroll, setHideScroll] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(0);
  const [openDualAccountModal, setOpenDualAccountModal] = useState(false);
  const [switchAccountLoading, setSwitchAccountLoading] = useState(false);

  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);
  const isXs = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        const viewportWidth = window.innerWidth - 80;
        setWidth(Math.min(newWidth, viewportWidth));
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const boards = accountDetails?.boards?.memberBoards || [];
    setMemberBoards(boards);
  }, [accountDetails]);

  useEffect(() => {
    const handleBoardJoined = (board) => {
      setMemberBoards((prev) => {
        const exists = prev.some((item) => item.id === board.id);
        if (exists) return prev;
        return [...prev, board];
      });
    };

    eventBus.on(eventBusValues.advisorAnsweredInvitationV2, handleBoardJoined);
    return () => {
      eventBus.remove(
        eventBusValues.advisorAnsweredInvitationV2,
        handleBoardJoined
      );
    };
  }, []);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollWidth } = container;
    if (scrollWidth <= width) {
      setHideScroll(true);
    } else {
      setHideScroll(false);
    }
  }, [width, memberBoards, isXs]);

  const sortedBoards = useMemo(() => {
    return [...memberBoards].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : 0;
      const dateB = b.createdAt ? new Date(b.createdAt) : 0;
      return dateB - dateA;
    });
  }, [memberBoards]);

  const totalBoards = sortedBoards.length;
  let displayedBoards = sortedBoards;

  // if (isXs && !expanded) {
  //   if (totalBoards > 2) {
  //     displayedBoards = sortedBoards.slice(0, 2);
  //   }
  // }

  const showMoreButton = isXs && totalBoards > 2;

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const jump = 300;

    if (scrollLeft + clientWidth + 50 >= scrollWidth) {
      container.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      container.scrollBy({
        left: jump,
        behavior: 'smooth',
      });
    }
  };

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const jump = 300;
    if (container.scrollLeft === 0) {
      container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -jump, behavior: 'smooth' });
    }
  };

  if (!accountDetails) return null;

  const memberBoardCards = displayedBoards.map((board, idx) => (
    <Box
      key={`member-board-${board.id || idx}`}
      sx={{
        marginBottom: '20px',
        paddingTop: '10px',
        marginRight: '16px',
        width: { xxs: '280px', xs: '350px' },
      }}
    >
      <BoardCard
        board={board}
        companyList
        width={isXs ? '280px' : '350px'}
        displayPillsFlag
      />
    </Box>
  ));

  const emptyStateCard = (
    <Box
      key="empty-state"
      sx={{
        marginBottom: '20px',
        paddingTop: '10px',
        marginRight: '16px',
        width: { xxs: '280px', xs: '350px' },
      }}
    >
      <BoardCard
        board={{
          companyName: 'Join Advisory Boards!',
          headline:
            'Our platform makes it easy and rewarding to be on boards built by 100s of organizations on AdvisoryCloud.',
        }}
        width={isXs ? '280px' : '350px'}
        headlineMarginBottom="40px"
        staticTile
        companyList
        buttonText="Explore Boards"
        onClick={scrollClick}
      />
    </Box>
  );

  const dualAccountCard = (
    <Box
      key="dual-account"
      sx={{
        marginBottom: '20px',
        paddingTop: '10px',
        marginRight: '16px',
        width: { xxs: '280px', xs: '350px' },
      }}
    >
      <BoardCard
        board={{
          ...(accountDetails.companyDual || {}),
          companyName:
            isDualUser && accountDetails.companyDual?.companyName
              ? accountDetails.companyDual.companyName
              : `${accountDetails.givenName}'s Advisory Board`,
          headline:
            isDualUser && accountDetails.companyDual?.headline
              ? accountDetails.companyDual.headline
              : 'Included with your membership is the ability to start your own advisory board for a company, team or personal side project.',
        }}
        buttonText={
          isDualUser
            ? switchAccountLoading
              ? 'Switching...'
              : 'Switch to Company'
            : 'Get Started'
        }
        hideMemberSince
        Chip={!isDualUser ? IncludedChip : null}
        companyList
        width={isXs ? '280px' : '350px'}
        staticTile
        customImage={accountDetails.companyDual?.image?.id ? null : calendar}
        headlineMarginBottom="40px"
        onClick={async () => {
          if (isDualUser) {
            setSwitchAccountLoading(true);
            const user = await Auth.currentAuthenticatedUser();
            await Auth.updateUserAttributes(user, {
              profile: AccountType.COMPANY,
            });
            await authenticate();
          } else {
            setOpenDualAccountModal(true);
          }
        }}
      />
    </Box>
  );

  const boardItems = [
    ...(totalBoards === 0 ? [emptyStateCard] : memberBoardCards),
    dualAccountCard,
  ];

  return (
    <Paper
      id="my-advisory-boards-tile"
      ref={containerRef}
      elevation={1}
      sx={{
        padding: '15px',
        paddingRight: { xxs: '26px', xs: '15px' },
        borderRadius: '12px',
        // marginBottom: '20px',
        boxShadow: 'none',
        maxWidth: '100%',
        position: 'relative',
        // border: '1px solid #E0E0E0',
      }}
    >
      <DualAccountModal
        open={openDualAccountModal}
        setOpen={setOpenDualAccountModal}
      />
      <Typography
        sx={{ fontWeight: 600, fontSize: '20px', mb: '4px', color: '#001030' }}
      >
        💼 My Advisory Boards
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 14,
          color: '#4D586E',
          fontStyle: 'italic',
          mb: '16px',
        }}
      >
        AdvisoryCloud makes sharing your ideas across your portfolio of boards
        easy, exciting and rewarding
      </Typography>

      {/* {!hideScroll && boardItems.length > 1 && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            left: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )} */}

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'visible',
          whiteSpace: 'nowrap',
          scrollBehavior: 'smooth',
          overscrollBehaviorX: 'contain',
          marginRight: { xxs: 0, xs: hideScroll ? 0 : '40px' },
          maxWidth: {
            xxs: '100%',
            xs: width - 40,
            md: width + 200,
            lg: width + 500,
          },
        }}
      >
        {boardItems}
      </Box>

      {!hideScroll && boardItems.length > 1 && (
        <IconButton
          onClick={scrollRight}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            right: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {/* {showMoreButton && (
        <Box sx={{ textAlign: 'center', mt: '0px' }}>
          <Button
            variant="text"
            onClick={() => setExpanded(!expanded)}
            sx={{
              border: '1px solid #2C71F64D',
              borderRadius: '8px',
              color: '#2C71F6',
              backgroundColor: '#fff',
              p: '4px 16px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              width: '100%',
              height: '40px',
              '&:hover': { backgroundColor: '#F3F4F6' },
            }}
          >
            {expanded ? 'Less' : 'More'}
          </Button>
        </Box>
      )} */}
    </Paper>
  );
};

export default MyAdvisoryBoards;
